"use client";

import * as React from "react";
import { cn } from "../../lib/utils";
import { CheckCircledIcon, ValueNoneIcon } from "@radix-ui/react-icons";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";
import { Input } from "./input";

// This could be moved in a shared config/globales package. But as this list is
// required in several places in the app, and the ui package is one of the most
// used, it's better to keep it here for now.
export const AVAILABLE_LOCALES = {
  en: { value: "en", label: "Anglais", flag: "🇬🇧" },
  fr: { value: "fr", label: "Français", flag: "🇫🇷" },
  es: { value: "es", label: "Espagnol", flag: "🇪🇸" },
  de: { value: "de", label: "Allemand", flag: "🇩🇪" },
  it: { value: "it", label: "Italien", flag: "🇮🇹" },
  nl: { value: "nl", label: "Néerlandais", flag: "🇳🇱" },
  pt: { value: "pt", label: "Portugais", flag: "🇵🇹" },
  sv: { value: "sv", label: "Suédois", flag: "🇸🇪" },
  fi: { value: "fi", label: "Finnois", flag: "🇫🇮" },
  no: { value: "no", label: "Norvégien", flag: "🇳🇴" },
  da: { value: "da", label: "Danois", flag: "🇩🇰" },
  pl: { value: "pl", label: "Polonais", flag: "🇵🇱" },
  hu: { value: "hu", label: "Hongrois", flag: "🇭🇺" },
  cs: { value: "cs", label: "Tchèque", flag: "🇨🇿" },
  sk: { value: "sk", label: "Slovaque", flag: "🇸🇰" },
  bg: { value: "bg", label: "Bulgare", flag: "🇧🇬" },
  zh: { value: "zh", label: "Chinois (Simplifié)", flag: "🇨🇳" },
  ja: { value: "ja", label: "Japonais", flag: "🇯🇵" },
};


export interface TranslatableFieldProps {
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  locales: string[];
  defaultLocale: string;
  placeholder?: string;
  className?: string;
  renderInput?: (props: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    currentLocale: string;
  }) => React.ReactNode;
}


export function TranslatableField({
  value = {},
  onChange,
  locales,
  defaultLocale,
  placeholder,
  className,
  renderInput,
}: TranslatableFieldProps) {
  const [currentLocale, setCurrentLocale] = React.useState<string>(
    defaultLocale || locales[0] || "en",
  );

  // Update current locale when defaultLocale changes
  React.useEffect(() => {
    if (defaultLocale && defaultLocale !== currentLocale) {
      setCurrentLocale(defaultLocale);
    }
  }, [defaultLocale]);

  // Ensure current locale is valid
  React.useEffect(() => {
    if (!locales.includes(currentLocale)) {
      setCurrentLocale(defaultLocale || locales[0] || "en");
    }
  }, [locales, currentLocale, defaultLocale]);

  const currentLocaleConfig = AVAILABLE_LOCALES[currentLocale as keyof typeof AVAILABLE_LOCALES];
  const currentValue = value[currentLocale] || "";

  const handleLocaleChange = (newLocale: string) => {
    setCurrentLocale(newLocale);
  };

  const handleInputChange = (newValue: string) => {
    onChange({
      ...value,
      [currentLocale]: newValue,
    });
  };

  const getTranslationState = (locale: string): "empty" | "full" => {
    const localeValue = value[locale] || "";
    return localeValue.length > 0 ? "full" : "empty";
  };

  const LocaleSelector = () => (
    <div className="absolute right-1 top-1 z-10">
      <Select value={currentLocale} onValueChange={handleLocaleChange}>
        <SelectContent>
          {locales.map((locale) => {
            const config = AVAILABLE_LOCALES[locale as keyof typeof AVAILABLE_LOCALES];
            const state = getTranslationState(locale);
            const StateIcon = state === "full" ? CheckCircledIcon : ValueNoneIcon;
            const stateColor = state === "full" ? "text-green-300" : "text-muted-foreground/80";

            return (
              <SelectItem key={locale} value={locale}>
                <div className="flex items-center gap-2">
                  <span className="text-base">{config?.flag || "🌐"}</span>
                  <span>{config?.label || locale}</span>
                  <StateIcon className={cn("size-4", stateColor)} />
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
        <SelectTrigger className="h-7 w-8 border-0 bg-transparent px-2 shadow-none focus:ring-0 [&>svg]:hidden">
          <SelectValue>
            <span className="text-base">{currentLocaleConfig?.flag || "🌐"}</span>
          </SelectValue>
        </SelectTrigger>
      </Select>
    </div>
  );

  if (!renderInput) {
    return (
      <div className={cn("relative", className)}>
        <Input
          type="text"
          value={currentValue}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder={placeholder}
          className="pr-12"
        />
        <LocaleSelector />
      </div>
    );
  }

  return (
    <div className={cn("relative", className)}>
      {renderInput({
        value: currentValue,
        onChange: handleInputChange,
        placeholder,
        currentLocale,
      })}
      <LocaleSelector />
    </div>
  );
}
